<template lang="pug">
div
	v-row(no-gutters)
		v-col.grow
			h1 {{topic.title}}
			i18n.subtitle-1(path="page.forum.topic:subject.about.destination.heading" v-if="topic.destinations.length")
				glue-list(slot="name" glue=", " :lastglue="` ${$t('global.and')} `")
					magic-nuxt-link(v-for="destination in topic.destinations" :key="destination.id" :to="destination" :title="destination.name") {{destination.name}}
		v-col.shrink.text-no-wrap(v-if="showMarkAsSoldButton")
			v-dialog(v-model="showConfirmSold" max-width="500")
				template(v-slot:activator="{ on }")
					v-btn.mr-4(text v-on="on" :disabled="topic.isItemSold")
						v-icon remove_shopping_cart
				v-card
					v-container
						v-card-text {{$t('topic:mark.as.sold.confirm')}}
						v-card-actions
							v-spacer
							v-btn(color="primary" text @click="showConfirmSold = false") {{$t('topic:confirm.button.no')}}
							v-btn(color="primary" @click="markAsSold") {{$t('topic:confirm.button.yes')}}
		v-col.shrink.text-no-wrap(v-if="$store.state.user.id")
			v-checkbox.mt-0(v-model="topic.isFavorite" on-icon="favorite" off-icon="favorite_border" color="#ff5a5e" :label="`${topic.followerCount}`" hide-details)

	template(v-if="pageCount > 1")
		v-pagination.mb-6( v-model="mutablePage" :key="`top-${mutablePage}`" :length="pageCount" :total-visible="10")
		v-divider.mb-6

	div(v-if="page === 1" )
		user-content(v-bind="topic" :date="topic.created_at" :quotable="!!$store.state.user.id" editable @quote="createQuote" type="topic" :adminPath="topic.meta.adminPath")
		template(v-if="topic.editorialTip")
			v-alert.my-6(type="info" color="accent" text border="bottom")
				strong {{$t('topic:editorial.tip.title')}}
				div(v-html="topic.editorialTip")
			v-divider
	v-data-iterator.reply-list.mb-6(:items="topic.replies.nodes" :items-per-page="30" hide-default-footer :page.sync="mutablePage")
		template(#no-data)
			.mb-6
		template(#item="{item, index}")
			user-content(v-bind="item" :date="item.created_at" :quotable="!!$store.state.user.id" editable @quote="createQuote" type="reply" :adminPath="item.meta.adminPath")
			template(v-if="index === 10 && topic.replies.nodes.length > 12")
				ad(location="replies").mb-6
	template(v-if="skeleton")
		v-skeleton-loader.mb-3(type="paragraph" class="mx-auto")
		v-divider.mb-6

	template(v-if="pageCount > 1")
		v-pagination.mb-6( v-model="mutablePage" :key="`bottom-${mutablePage}`" :length="pageCount" :total-visible="10")
		v-divider.mb-6

	h2 {{$t('page.forum.topic:reply.title')}}
	template(v-if="topic.isOldTopic")
		.flow-left
			v-alert.my-6(type="info" color="accent" text dense)
				i18n(path="page.forum.topic:closed.because.no.activity")
	template(v-else-if="topic.isItemSold")
		.flow-left
			v-alert.my-6(type="info" color="accent" text dense)
				i18n(path="page.forum.topic:closed.because.sold")
	template(v-else-if="topic.isClosed")
		.flow-left
			v-alert.my-6(type="info" color="accent" text dense)
				i18n(path="page.forum.topic:closed.explainer")
	template(v-else)
		reply-form#replyform(v-model="formContent" :hide-notify="topic.isFavorite" :parentId="topic.id" parentType="topic" @submit-reply="newReply" v-bind:isposting.sync="skeleton" ref="topicReplyForm")

</template>

<script>
// eslint rule disabled, de huidige prop mutations gebeuren bij vrij specifieke dingen (markeren als verkocht, nieuewe reply)
// de moeite om het component om te schrijven weegt voor nu (oktober 2020)
/* eslint-disable vue/no-mutating-props */

import gql from 'graphql-tag';
import ReplyForm from '@/components/forum/reply-form';
import ForumContentEditor from '@/components/forum/forum-content-editor';
import UserContent from '@/components/user-content/item.vue';
import MetaFragment from '@/components/graphql/metaFragment.graphql';

export default {
	name: 'ForumTopicComponent',
	components: {
		ReplyForm,
		ForumContentEditor,
		UserContent
	},
	props: {
		topic: {
			type: Object,
			required: true
		},
		replyCount: {
			type: Number,
			required: true
		},
		page: {
			type: Number,
			required: true
		}
	},
	data() {
		return {
			mutablePage: 1,
			skeleton: false,
			formContent: null,
			showConfirmSold: false
		};
	},
	computed: {
		isFavorite() {
			return this.topic.isFavorite;
		},
		pageCount() {
			return Math.ceil(this.replyCount / 30) || 1;
		},
		showMarkAsSoldButton() {
			return (
				this.$store.state.user.id &&
				this.$store.state.user.id === this.topic.user.id &&
				this.topic.category === 'marketplace'
			);
		}
	},
	watch: {
		mutablePage: {
			handler(value) {
				this.$emit('update:page', value);
			}
		},
		page: {
			handler(value) {
				this.mutablePage = value;
			}
		},
		isFavorite(val) {
			this.updateFavorite(val);
		}
	},
	created() {
		this.mutablePage = this.page;
	},
	mounted() {
		if (this.$route.hash === '#last')
			this.$vuetify.goTo('.reply-list .user-content:last-child', {
				duration: 0,
				offset: 100
			});
	},
	methods: {
		createQuote(reply) {
			this.formContent =
				'> ' +
				reply.content.rawMarkdown.split('\n').join('\n> ') +
				`\n> \n> § ${this.$t('reply:quote.posted.by', {
					user: reply.user.username,
					date: this.$dayjs(reply.date).format('DD MMM YYYY HH:mm')
				})}\n>\n`;

			this.$vuetify.goTo('#replyform');
			this.$refs.topicReplyForm.$refs.forumcontent.focusTextArea();
		},
		updateFavorite(val) {
			const mutation = val
				? gql`
						mutation ($topicId: ID!) {
							topicdata: addFavoriteTopic(topicId: $topicId) {
								followerCount
							}
						}
				  `
				: gql`
						mutation ($topicId: ID!) {
							topicdata: removeFavoriteTopic(topicId: $topicId) {
								followerCount
							}
						}
				  `;

			if (this.$store.state.user.id) {
				this.$graphClient
					.mutate({
						mutation,
						errorPolicy: 'all',
						variables: {
							topicId: this.topic.id
						}
					})
					.then(({ data, errors }) => {
						this.topic.followerCount = data.topicdata.followerCount;
						if (errors) {
							throw errors[0];
						}
					})
					.catch(_error => {
						this.$toast.error(this.$t('reply:upvote.error'));
					});
			}
		},
		newReply(reply) {
			if (this.page === this.pageCount) {
				this.topic.replies.nodes.push(reply);
			} else {
				this.topic.replyCount++;
				if (this.topic.replyCount >= 30) {
					this.$router.push({
						path: this.$route.path,
						query: { page: this.pageCount }
					});
				}
			}
		},
		async markAsSold() {
			if (this.$store.state.user.id) {
				await this.$graphClient
					.mutate({
						mutation: gql`
							mutation ($topicId: ID!) {
								sold(topicId: $topicId)
							}
						`,
						errorPolicy: 'all',
						variables: {
							topicId: this.topic.id
						}
					})
					.then(({ _data, _errors }) => {
						this.topic.isItemSold = true;
					})
					.catch(_error => {
						this.$toast.error(this.$t('topic:marked.as.sold.error'));
					});
			}

			this.showConfirmSold = false;
		}
	},
	fragments: gql`
		fragment TopicDataFragment on Topic {
			...UserContentDetails
			id
			slug
			title
			category
			destinations {
				name
				slug
			}
			followerCount
			isClosed
			isOldTopic
			isItemSold
			isFavorite
			editorialTip
			replyCount
			...MetaFragment
			replies(page: $page) {
				nodes {
					...UserContentDetails
				}
			}
			content {
				introduction(length: 160)
			}
		}
		${UserContent.fragments}
		${MetaFragment}
	`
};
</script>
